/**
 * EmptyState — la capa que no existía.
 *
 * El caller pide una SITUACIÓN ("no hay nada agendado"), no un dibujo. El
 * universo del tema activo decide el arte y el texto: en Netrunner sale una
 * cola de procesos vacía, en Niebla Gris un reloj de bolsillo detenido.
 *
 * La primera versión tenía un solo juego de ilustraciones —victoriano— que
 * salía en todos los temas. Un reloj de bolsillo en un tema del Net rompe
 * la ficción igual que rompería una scanline CRT en el Club de Tarot.
 */

import { useTheme } from "../../hooks/useTheme";
import { getUniverse } from "../../themes";
import { emptyCopy, type EmptySlot } from "../../themes/lexicon";
import type { Universe } from "../../themes";

type ArtSet = Record<EmptySlot, React.ReactNode>;

/* ── Cyberpunk — angular, técnico, circuito ──────────────────────────── */
const CYBERPUNK: ArtSet = {
  // Shard de datos: chip hexagonal sin contenido
  "no-items": (
    <>
      <path d="M52 12 L82 29 V79 L52 96 L22 79 V29 Z" className="bw-ln" />
      <path d="M52 24 L72 35 V71 L52 82 L32 71 V35 Z" className="bw-ln2" />
      <path d="M44 47 h16 v16 h-16 z" className="bw-ln" />
      <circle cx="52" cy="55" r="2.2" fill="var(--bw-accent)" />
      <g className="bw-ln" opacity="0.55">
        <path d="M52 47 v-9" /><path d="M52 63 v9" />
        <path d="M44 55 h-9" /><path d="M60 55 h9" />
      </g>
      <g className="bw-ln2"><path d="M22 40 h-8" /><path d="M22 58 h-8" /><path d="M82 40 h8" /><path d="M82 58 h8" /></g>
    </>
  ),
  // Cola de procesos: tres slots vacíos esperando trabajo
  "no-schedule": (
    <>
      {[24, 54, 84].map((y, i) => (
        <path
          key={y}
          d={`M18 ${y} L24 ${y - 6} H80 L86 ${y} V${y + 14} L80 ${y + 20} H24 L18 ${y + 14} Z`}
          className={i === 0 ? "bw-ln" : "bw-ln2"}
          strokeDasharray={i === 0 ? undefined : "4 4"}
        />
      ))}
      <g className="bw-ln" opacity="0.7">
        <path d="M44 32 h16" /><path d="M52 24 v16" />
      </g>
      <path d="M40 62 h24" className="bw-ln2" opacity="0.5" />
      <path d="M40 92 h24" className="bw-ln2" opacity="0.35" />
    </>
  ),
  // Puerto neural sin cable conectado
  "no-sessions": (
    <>
      <path d="M24 36 L34 26 H70 L80 36 V86 L70 96 H34 L24 86 Z" className="bw-ln" />
      <path d="M40 50 L44 46 H60 L64 50 V72 L60 76 H44 L40 72 Z" className="bw-ln2" />
      <g className="bw-ln" opacity="0.7">
        <path d="M46 56 h12" /><path d="M46 61 h12" /><path d="M46 66 h12" />
      </g>
      <circle cx="52" cy="38" r="1.8" fill="var(--bw-accent)" />
      <g className="bw-ln2"><path d="M30 106 h14" strokeDasharray="3 4" /><path d="M60 106 h14" strokeDasharray="3 4" /></g>
      <path d="M46 100 l6 6 l6 -6" className="bw-ln" opacity="0.5" />
    </>
  ),
  // Antena con la onda cortada
  "no-signal": (
    <>
      <path d="M52 96 V52" className="bw-ln" />
      <path d="M34 96 h36" className="bw-ln" />
      <circle cx="52" cy="46" r="5" className="bw-ln" />
      <circle cx="52" cy="46" r="1.8" fill="var(--bw-accent)" />
      <g className="bw-ln" opacity="0.7">
        <path d="M38 34 L52 22 L66 34" />
        <path d="M30 26 L52 8 L74 26" strokeDasharray="4 5" opacity="0.5" />
      </g>
      <path d="M40 60 l24 24 M64 60 l-24 24" className="bw-ln" opacity="0.32" />
    </>
  ),
};

/* ── Arcane — line-art victoriano, ocultismo, gas y vapor ────────────── */
const ARCANE: ArtSet = {
  // Carta de tarot con ojo
  "no-items": (
    <>
      <rect x="18" y="10" width="68" height="98" rx="5" className="bw-ln" />
      <rect x="25" y="17" width="54" height="84" rx="3" className="bw-ln2" />
      <ellipse cx="52" cy="55" rx="20" ry="12" className="bw-ln" />
      <circle cx="52" cy="55" r="6" className="bw-ln" />
      <circle cx="52" cy="55" r="2.4" fill="var(--bw-accent)" />
      <g className="bw-ln" opacity="0.55">
        <path d="M52 33 v-7" /><path d="M52 84 v7" />
        <path d="M28 55 h-7" /><path d="M76 55 h7" />
        <path d="M35 38 l-5 -5" /><path d="M69 38 l5 -5" />
        <path d="M35 72 l-5 5" /><path d="M69 72 l5 5" />
      </g>
    </>
  ),
  // Reloj de bolsillo detenido
  "no-schedule": (
    <>
      <circle cx="52" cy="66" r="34" className="bw-ln" />
      <circle cx="52" cy="66" r="28" className="bw-ln2" />
      <path d="M46 26 h12 v6 h-12z" className="bw-ln" />
      <path d="M52 48 v18 l13 8" className="bw-ln" />
      <circle cx="52" cy="66" r="2.6" fill="var(--bw-accent)" />
      <g className="bw-ln2">
        <path d="M52 42 v4" /><path d="M76 66 h-4" />
        <path d="M52 90 v-4" /><path d="M28 66 h4" />
      </g>
      <path d="M58 30 q16 -6 22 6 q4 8 -2 12" className="bw-ln" strokeDasharray="3 4" opacity="0.6" />
    </>
  ),
  // Puerta entre bandas de niebla
  "no-sessions": (
    <>
      <path d="M34 96 V46 a18 18 0 0 1 36 0 v50" className="bw-ln" />
      <path d="M42 96 V47 a10 10 0 0 1 20 0 v49" className="bw-ln2" />
      <path d="M20 96 h64" className="bw-ln" />
      <g className="bw-ln" opacity="0.75">
        <path d="M10 72 q14 -7 28 0 t28 0 t28 0" />
        <path d="M10 82 q14 -7 28 0 t28 0 t28 0" opacity="0.6" />
        <path d="M14 62 q12 -6 24 0 t24 0" opacity="0.4" />
      </g>
    </>
  ),
  // Vela apagada con hilo de humo
  "no-signal": (
    <>
      <path d="M42 58 h20 v40 h-20 z" className="bw-ln" />
      <path d="M34 98 h36" className="bw-ln" />
      <path d="M42 64 q10 4 20 0" className="bw-ln2" />
      <path d="M52 58 v-7" className="bw-ln" />
      <g className="bw-ln" opacity="0.6" strokeDasharray="3 4">
        <path d="M52 48 q9 -7 0 -15 q-9 -8 0 -16" />
      </g>
      <circle cx="52" cy="51" r="1.6" fill="var(--bw-accent)" />
    </>
  ),
};

const ART_BY_UNIVERSE: Record<Universe, ArtSet> = {
  cyberpunk: CYBERPUNK,
  arcane: ARCANE,
};

interface EmptyStateProps {
  /** Qué situación es, no qué dibujar. El tema elige el arte. */
  slot: EmptySlot;
  /** Override del título del léxico. Úsalo solo si el default no encaja. */
  title?: string;
  /** Override del hint — típico cuando hace falta decir a dónde ir. */
  hint?: string;
  action?: React.ReactNode;
  compact?: boolean;
}

export function EmptyState({ slot, title, hint, action, compact = false }: EmptyStateProps) {
  const { theme } = useTheme();
  const universe = getUniverse(theme);
  const copy = emptyCopy(universe, slot);
  const size = compact ? 78 : 104;

  return (
    <div className={`flex flex-col items-center text-center ${compact ? "py-5" : "py-9"}`}>
      <svg
        width={size}
        height={size * 1.13}
        viewBox="0 0 104 118"
        aria-hidden="true"
        className="mb-4 [&_.bw-ln]:fill-none [&_.bw-ln]:stroke-[var(--bw-accent)] [&_.bw-ln]:[stroke-width:1.1] [&_.bw-ln]:[stroke-linecap:round] [&_.bw-ln]:[stroke-linejoin:round] [&_.bw-ln2]:fill-none [&_.bw-ln2]:stroke-[var(--bw-muted)] [&_.bw-ln2]:[stroke-width:1] [&_.bw-ln2]:opacity-65"
      >
        {ART_BY_UNIVERSE[universe][slot]}
      </svg>
      <p className="bw-display text-[var(--bw-fg)]" style={{ fontSize: compact ? 16 : 19 }}>
        {title ?? copy.title}
      </p>
      <p className="bw-prose text-[13px] text-[var(--bw-muted)] mt-1.5 max-w-[36ch]">
        {hint ?? copy.hint}
      </p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
